import { FC, useCallback, useMemo, useState } from 'react';
import { MRT_Row, MRT_RowData, MRT_SortingState } from 'material-react-table';

import { ContainerChargeDto } from 'src/generated/services/TFinancialApi';
import { useUpsertContainerCharge } from 'src/use-cases/serviceProvider/useServiceProvider';
import { ContractManagerColumnHeader } from '../ContractManagerColumnHeader';
import { RateCardChargesTableBase } from './TableBase';
import { getTableColumns } from './utils';
import { ITableProps } from './types';
import { applyFiltersAndSorting, createFilterOptions, commonBooleanAccessors } from './filterUtils';

export const RateCardChargesTableContainer: FC<ITableProps> = (props) => {
  const { contractFlow, chargeCategory, tableRef, filters, setFilters, sorting, setSorting, isAdmin } = props;
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  // Add filters state

  // Original unfiltered data
  const originalData = useMemo(() => contractFlow?.[chargeCategory] ?? [], [contractFlow, chargeCategory]);

  // Define accessors for boolean fields
  const accessors = useMemo(() => commonBooleanAccessors, []);

  // Apply filters with accessors
  const tableData = useMemo(() => applyFiltersAndSorting(originalData, filters, accessors, sorting), [originalData, filters, accessors, sorting]);

  // Handle sorting and filtering
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      setSorting([{ id: columnId, desc: direction === 'desc' }]);
    },
    [setSorting]
  );

  // Handle filter changes
  const handleFilter = useCallback(
    (columnId: string, selectedValues: string[]) => {
      setFilters((prev) => ({
        ...prev,
        [columnId]: selectedValues,
      }));
    },
    [setFilters]
  );

  const columns = useMemo(() => {
    const containerColumns = [
      {
        accessorKey: 'unitPrice20',
        header: 'UNIT PRICE 20',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='unitPrice20'
            originalData={originalData}
            currentFilters={filters.unitPrice20 || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
      },
      {
        accessorKey: 'unitPrice40',
        header: 'UNIT PRICE 40',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='unitPrice40'
            originalData={originalData}
            currentFilters={filters.unitPrice40 || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
      },
      {
        accessorKey: 'currency',
        header: 'CURRENCY',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='currency'
            originalData={originalData}
            currentFilters={filters.currency || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
      },
      {
        accessorKey: 'containerType',
        header: 'CONTAINER TYPE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='containerType'
            originalData={originalData}
            currentFilters={filters.containerType || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
      },
    ];

    const allColumns = getTableColumns(containerColumns, validationErrors, setValidationErrors, {
      filters,
      handleFilter,
      handleSort: handleTableSort,
      originalData,
      createFilterOptions,
    });

    return allColumns;
  }, [validationErrors, originalData, handleFilter, handleTableSort, filters]);

  const { mutateAsync: upsertContainerCharge } = useUpsertContainerCharge();

  const onValidateCharge = useCallback((charge: Record<string, any>) => {
    const validationResult = {
      chargeName: !charge.chargeName || charge.chargeName === '' ? 'Required' : '',
      chargeCategory: !charge.chargeCategory || charge.chargeCategory === '' ? 'Required' : '',
      legType: !charge.legType || charge.legType === '' ? 'Required' : '',
      currency: !charge.currency || charge.currency === '' ? 'Required' : '',
      unitPrice20: (!charge.unitPrice20 || charge.unitPrice20 === '') && charge.unitPrice20 !== 0 ? 'Required' : '',
      unitPrice40: (!charge.unitPrice40 || charge.unitPrice40 === '') && charge.unitPrice40 !== 0 ? 'Required' : '',
    };

    return validationResult;
  }, []);

  const onUpsertCharge = useCallback(
    async (row: MRT_Row<MRT_RowData>, values: Record<string, any>) => {
      const reqBody: ContainerChargeDto & { contractFlowId: string; deviationExcluded?: boolean } = {
        id: row.original?.id || null,
        contractFlowId: contractFlow?.id,
        legType: values.legType,
        chargeName: values.chargeName,
        chargeCategory: values.chargeCategory,
        accessorial: values.accessorial.toLowerCase() === 'true',
        approval: values.approval.toLowerCase() === 'true',
        outlay: values.outlay.toLowerCase() === 'true',
        deviationExcluded: values.deviationExcluded ? values.deviationExcluded.toLowerCase() === 'true' : false,
        containerType: values.containerType || null,
        unitPrice20: values.unitPrice20,
        unitPrice40: values.unitPrice40,
        currency: values.currency || null,
      };

      await upsertContainerCharge(reqBody);
    },
    [upsertContainerCharge, contractFlow]
  );

  return (
    <RateCardChargesTableBase
      tableRef={tableRef}
      tableData={tableData}
      columns={columns}
      sorting={sorting}
      contractFlow={contractFlow}
      chargeCategory={chargeCategory}
      onUpsertCharge={onUpsertCharge}
      setValidationErrors={setValidationErrors}
      onValidateCharge={onValidateCharge}
      isAdmin={isAdmin}
    />
  );
};
